import { DIALOG_LEAVE_ANIMATION_DURATION } from '@/config';
import {
  Description,
  Dialog as BaseDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Fragment, ReactNode } from 'react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  showCloseButton?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg';
  overlayClassName?: string;
};

export const Dialog = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  showCloseButton = true,
  maxWidth = 'sm',
  overlayClassName = 'bg-black/50',
}: Props) => {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <BaseDialog className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={`ease-in duration-${DIALOG_LEAVE_ANIMATION_DURATION}`}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`fixed inset-0 ${overlayClassName}`} />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={`relative w-full ${maxWidthClasses[maxWidth]} overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all`}
              >
                {(title || showCloseButton) && (
                  <div className="mb-4 flex items-start justify-between">
                    <div>
                      {title && (
                        <DialogTitle
                          as="h3"
                          className="text-xl font-medium leading-7 text-rw-neutral-rich-black"
                        >
                          {title}
                        </DialogTitle>
                      )}
                      {description && (
                        <Description className="mt-1.5 text-sm text-rw-neutral-gray">
                          {description}
                        </Description>
                      )}
                    </div>
                  </div>
                )}
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </BaseDialog>
    </Transition>
  );
};
